import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import { MainLogo } from "./MainLogo";
import { BREAKPOINT, SM, TRANSITION, XL } from "./tokens";
import { IconButton } from "../../components/IconButton";
import {
  ActivityIcon,
  CalendarIcon,
  ClockStopwatchIndicatorIcon,
  CloseIcon,
  CollapsIcon,
  EstimatesIcon,
  InvoicesIcon,
  MobileMenuIcon,
  MyWorkIcon,
  NotificationBellIcon,
  PeopleIcon,
  ProjectsIcon,
  ReportTimeIcon,
  ReportsIcon,
  RocketIcon,
  SearchIcon,
} from "../../components/Icons";
import { Modal } from "../../components/Modal";
import { Paper } from "../../components/Paper";
import { Tooltip } from "../../components/Tooltip";
import { Body2, Caption1 } from "../../components/Typography";
import { DARK_THEME_SELECTOR, useResizeObserver } from "../../utils";

const MENU_ITEMS: {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
  active?: boolean;
}[] = [
  { icon: RocketIcon, text: "Getting Started" },
  { icon: ProjectsIcon, text: "Projects", active: true },
  { icon: MyWorkIcon, text: "My Work" },
  { icon: ActivityIcon, text: "Activity" },
  { icon: CalendarIcon, text: "Calendar" },
  { icon: PeopleIcon, text: "People" },
  { icon: ReportTimeIcon, text: "Time" },
  { icon: ReportsIcon, text: "Reports" },
  { icon: InvoicesIcon, text: "Invoices" },
  { icon: EstimatesIcon, text: "Estimates" },
];

/* ------------------------------------------------------------------ */
/* Layout shell (mirrors Layout.js)                                    */
/* ------------------------------------------------------------------ */

const StyledLayout = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--body-bg-main);
`;

const StyledContentRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row-reverse;
  flex: 1 1 0%;
  flex-grow: 1;
  overflow-y: auto;
`;

const StyledRightWindow = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  flex-grow: 1;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 0 16px;

  @media (min-width: ${SM}) {
    padding-right: 32px;
  }
`;

/* ------------------------------------------------------------------ */
/* Main menu (mirrors MainMenu.js + MainMenu.less)                     */
/* ------------------------------------------------------------------ */

const StyledMainMenu = styled.div`
  position: relative;
  z-index: 1;
  display: none;
  flex-direction: column;
  height: 100%;
  margin-right: 4px;
  flex-shrink: 0;
  width: 240px;
  transition: width ${TRANSITION};

  @media (min-width: ${SM}) {
    display: flex;
  }

  &.collapsed {
    width: 72px;
  }

  .menu-item-text,
  .menu-section,
  #logotype {
    transition: all ${TRANSITION};
  }

  &.collapsed .menu-item-text {
    opacity: 0;
    visibility: hidden;
    width: 0;
    margin-left: 0;
  }

  &.collapsed .menu-section {
    transform: scaleY(0);
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
  }

  &.collapsed #logotype {
    transform: translateX(-5%);
    opacity: 0;
  }

  &.collapsed .collapse-expand-menu svg {
    transform: rotate(180deg);
  }

  &:not(.mobile) {
    @media (max-width: 1279.98px) {
      width: 72px;

      .menu-item-text {
        opacity: 0;
        visibility: hidden;
        width: 0;
        margin-left: 0;
      }

      .menu-section {
        transform: scaleY(0);
        opacity: 0;
        max-height: 0;
        margin-bottom: 0;
      }

      #logotype {
        transform: translateX(-5%);
        opacity: 0;
      }
    }
  }

  &.mobile {
    display: flex;
    position: absolute;
    left: 0;
    top: 0;
    width: 240px;
    background-color: var(--page-paper-main);
  }
`;

const StyledMenuHeader = styled.div`
  position: relative;
  height: 68px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;

  .main-logo {
    position: absolute;
    left: 20px;
    color: var(--color-secondary);

    path {
      fill: var(--color-secondary);
    }
  }

  .collapse-expand-menu {
    position: absolute;
    right: 4px;
    display: none;

    @media (min-width: ${XL}) {
      display: flex;
    }
  }
`;

const StyledMenuScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  margin: 12px 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledMenuSection = styled.div`
  display: flex;
  align-items: center;
  margin: 0 12px 8px;
  padding: 0 4px 0 12px;
`;

const StyledMenuItem = styled.a`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 4px 0 12px;
  margin: 0 12px 2px;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;

  svg {
    flex-shrink: 0;
    fill: var(--color-theme-700);
    opacity: 0.7;
  }

  .menu-item-text {
    margin-left: 12px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    transition: all ${TRANSITION};
    background-color: var(--color-theme-300);

    svg {
      fill: var(--color-theme-900);
      opacity: 1;
    }

    .menu-item-text {
      color: var(--color-theme-900);
    }
  }

  &.active {
    background-color: var(--color-secondary-200);

    svg {
      fill: var(--color-secondary);
      opacity: 1;
    }

    .menu-item-text {
      color: var(--color-secondary);
    }
  }
`;

const StyledMobileMenuClose = styled(IconButton)`
  position: absolute;
  right: 16px;
`;

/* ------------------------------------------------------------------ */
/* Top bars                                                            */
/* ------------------------------------------------------------------ */

const StyledMobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: var(--color-primary);
  z-index: 50;

  @media (min-width: ${SM}) {
    display: none;
  }

  .mobile-menu-icon {
    fill: var(--color-theme-300);
  }
`;

const StyledIconRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTopRightControls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: none;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 32px 12px 0;

  @media (min-width: ${SM}) {
    display: flex;
  }
`;

const StyledHeaderAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: var(--color-secondary);
  color: var(--only-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  margin: 0 8px;
  flex-shrink: 0;
  cursor: pointer;

  /* dark themes: use paper colour for the initials */
  ${DARK_THEME_SELECTOR} & {
    color: var(--page-paper-main);
  }
`;

/* ------------------------------------------------------------------ */
/* Page region (mirrors Page.jsx / PageBody.jsx)                       */
/* ------------------------------------------------------------------ */

const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledPageBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-bottom: 32px;
  min-height: 0;
`;

/** The paper card that holds page content (used by list view & empty pages). */
export const StyledPagePaper = styled(Paper)`
  flex-grow: 1;
  width: 100%;
`;

/* ------------------------------------------------------------------ */
/* Mocked layout building blocks                                       */
/* ------------------------------------------------------------------ */

interface MenuItemsProps {
  mobile?: boolean;
}

const MenuItemsMock = ({ mobile = false }: MenuItemsProps): ReactElement => (
  <StyledMenuScroll>
    <StyledMenuSection className="menu-section">
      <Caption1 weight="bold" color="tertiary">
        MENU
      </Caption1>
    </StyledMenuSection>
    {MENU_ITEMS.map(({ icon: Icon, text, active }) => (
      <Tooltip key={text} title={text} placement="right" disable={mobile}>
        <StyledMenuItem className={active ? "active" : undefined}>
          <Icon />
          <Body2
            weight="medium"
            color="secondary"
            whitespace="no-wrap"
            className="menu-item-text"
          >
            {text}
          </Body2>
        </StyledMenuItem>
      </Tooltip>
    ))}
  </StyledMenuScroll>
);

interface MainMenuMockProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  footer?: ReactNode;
}

const MainMenuMock = ({
  collapsed,
  onToggleCollapsed,
  footer,
}: MainMenuMockProps): ReactElement => (
  <StyledMainMenu
    className={collapsed ? "collapsed" : undefined}
    data-testid="main-menu"
  >
    <StyledMenuHeader>
      <a className="main-logo">
        <MainLogo />
      </a>
      <div className="collapse-expand-menu">
        <IconButton
          variant="text gray"
          size="small"
          onClick={onToggleCollapsed}
        >
          <CollapsIcon />
        </IconButton>
      </div>
    </StyledMenuHeader>
    <MenuItemsMock />
    {footer}
  </StyledMainMenu>
);

interface MainMenuMobileMockProps {
  open: boolean;
  close: () => void;
  footer?: ReactNode;
}

const MainMenuMobileMock = ({
  open,
  close,
  footer,
}: MainMenuMobileMockProps): ReactElement | null =>
  open ? (
    <Modal open={open} onClose={close}>
      <StyledMainMenu className="mobile" data-testid="main-menu-mobile">
        <StyledMenuHeader>
          <a className="main-logo">
            <MainLogo />
          </a>
          <StyledMobileMenuClose onClick={close} variant="text gray">
            <CloseIcon />
          </StyledMobileMenuClose>
        </StyledMenuHeader>
        <MenuItemsMock mobile />
        {footer}
      </StyledMainMenu>
    </Modal>
  ) : null;

interface MobileMenuHeaderMockProps {
  onOpenMobileMenu: () => void;
}

const MobileMenuHeaderMock = ({
  onOpenMobileMenu,
}: MobileMenuHeaderMockProps): ReactElement => (
  <StyledMobileMenuHeader>
    <IconButton size="big" variant="circle raised" onClick={onOpenMobileMenu}>
      <MobileMenuIcon className="mobile-menu-icon" />
    </IconButton>
    <StyledIconRow>
      <IconButton variant="text gray">
        <SearchIcon />
      </IconButton>
      <StyledHeaderAvatar>JS</StyledHeaderAvatar>
      <IconButton variant="text gray">
        <NotificationBellIcon />
      </IconButton>
    </StyledIconRow>
  </StyledMobileMenuHeader>
);

const TopRightControlsMock = (): ReactElement => (
  <StyledTopRightControls>
    <IconButton variant="text gray">
      <ClockStopwatchIndicatorIcon />
    </IconButton>
    <IconButton variant="text gray">
      <SearchIcon />
    </IconButton>
    <StyledHeaderAvatar>JS</StyledHeaderAvatar>
    <IconButton variant="text gray">
      <NotificationBellIcon />
    </IconButton>
  </StyledTopRightControls>
);

/* ------------------------------------------------------------------ */
/* PresentationShell                                                   */
/* ------------------------------------------------------------------ */

export interface PresentationShellProps {
  /** Page header row (e.g. <ProjectPageHeader /> or <PageHeader title="…" />). */
  header: ReactNode;
  /** Page body content rendered inside the scrollable page area. */
  children: ReactNode;
  /** Optional fixed controls (e.g. <FloatingControls />). */
  floating?: ReactNode;
  /** Optional content pinned to the bottom of the main menu (e.g. <HelpAndSupportMenu />). */
  menuFooter?: ReactNode;
}

/**
 * The full application shell shared by every presentation mock-up: the main
 * menu on the left (auto-collapsing below 1280px, modal below 640px), the
 * top-right controls and the scrollable page area. Pass the page header via
 * `header` and the body via `children`.
 */
export const PresentationShell = ({
  header,
  children,
  floating,
  menuFooter,
}: PresentationShellProps): ReactElement => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isMobileView, setIsMobileView] = useState<boolean>();

  const ref = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(ref);
  const width = dimensions?.width;

  useEffect(() => {
    if (width) {
      if (width < BREAKPOINT) {
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
        setMobileMenuOpen(false);
      }
    }
  }, [width]);

  const handleOpenMobileMenu = useCallback(() => setMobileMenuOpen(true), []);
  const handleCloseMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const handleToggleCollapsed = useCallback(
    () => setCollapsed((prev) => !prev),
    []
  );

  return (
    <StyledLayout ref={ref}>
      {isMobileView ? (
        <MobileMenuHeaderMock onOpenMobileMenu={handleOpenMobileMenu} />
      ) : null}
      <StyledContentRow>
        <StyledRightWindow id="right-window">
          <StyledPage>
            {header}
            <StyledPageBody>{children}</StyledPageBody>
          </StyledPage>
          {isMobileView === false ? <TopRightControlsMock /> : null}
        </StyledRightWindow>
        {isMobileView ? (
          <MainMenuMobileMock
            open={mobileMenuOpen}
            close={handleCloseMobileMenu}
            footer={menuFooter}
          />
        ) : (
          <MainMenuMock
            collapsed={collapsed}
            onToggleCollapsed={handleToggleCollapsed}
            footer={menuFooter}
          />
        )}
      </StyledContentRow>
      {floating}
    </StyledLayout>
  );
};

PresentationShell.displayName = "PresentationShell";
